import { EndpointRegistry, HttpOptions, RegisteredHttpEndpoint } from '@luminovo/api-contracts';
import { getTokenIfPresent } from '@luminovo/auth';
import { http } from '@luminovo/http-client';
import {
    QueryClient,
    queryOptions,
    usePrefetchQuery,
    useQuery,
    UseQueryOptions,
    UseQueryResult,
    useSuspenseQuery,
    UseSuspenseQueryOptions,
    UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod/v4';

import { httpQueryKey } from './httpQueryKey';

export type UseHttpQueryOptions<
    T extends RegisteredHttpEndpoint,
    TData = z.infer<EndpointRegistry[T]['responseBody']>,
> = Pick<
    UseQueryOptions<z.infer<EndpointRegistry[T]['responseBody']>, Error, TData>,
    | 'enabled'
    | 'select'
    | 'placeholderData'
    | 'structuralSharing'
    | 'gcTime'
    | 'staleTime'
    | 'refetchInterval'
    | 'refetchOnMount'
    | 'refetchOnWindowFocus'
    | 'retry'
    | 'retryDelay'
    | 'notifyOnChangeProps'
    | 'meta'
    | 'throwOnError'
>;

export type UseSuspenseHttpQueryOptions<
    T extends RegisteredHttpEndpoint,
    TData = z.infer<EndpointRegistry[T]['responseBody']>,
> = Pick<
    UseSuspenseQueryOptions<z.infer<EndpointRegistry[T]['responseBody']>, Error, TData>,
    | 'select'
    | 'structuralSharing'
    | 'gcTime'
    | 'staleTime'
    | 'refetchInterval'
    | 'refetchOnMount'
    | 'refetchOnWindowFocus'
    | 'retry'
    | 'retryDelay'
    | 'notifyOnChangeProps'
    | 'meta'
>;

export function httpQuery<T extends RegisteredHttpEndpoint, TData = z.infer<EndpointRegistry[T]['responseBody']>>(
    endpoint: T,
    options: HttpOptions<T>,
    useQueryOptions: UseHttpQueryOptions<T, TData> = {},
): UseQueryOptions<z.infer<EndpointRegistry[T]['responseBody']>, Error, TData> {
    return queryOptions({
        queryKey: httpQueryKey(endpoint, options),
        queryFn: async () => http(endpoint, options, getTokenIfPresent()),
        ...useQueryOptions,
    });
}

export function suspenseHttpQuery<
    T extends RegisteredHttpEndpoint,
    TData = z.infer<EndpointRegistry[T]['responseBody']>,
>(
    endpoint: T,
    options: HttpOptions<T>,
    useQueryOptions: UseSuspenseHttpQueryOptions<T, TData> = {},
): UseSuspenseQueryOptions<z.infer<EndpointRegistry[T]['responseBody']>, Error, TData> {
    return queryOptions({
        queryKey: httpQueryKey(endpoint, options),
        queryFn: async () => http(endpoint, options, getTokenIfPresent()),
        ...useQueryOptions,
    });
}

export function useHttpQuery<T extends RegisteredHttpEndpoint, TData = z.infer<EndpointRegistry[T]['responseBody']>>(
    endpoint: T,
    options: HttpOptions<T>,
    useQueryOptions: UseHttpQueryOptions<T, TData> = {},
): UseQueryResult<TData, Error> {
    return useQuery(httpQuery(endpoint, options, useQueryOptions));
}

export function useSuspenseHttpQuery<
    T extends RegisteredHttpEndpoint,
    TData = z.infer<EndpointRegistry[T]['responseBody']>,
>(
    endpoint: T,
    options: HttpOptions<T>,
    useQueryOptions: UseSuspenseHttpQueryOptions<T, TData> = {},
): UseSuspenseQueryResult<TData, Error> {
    return useSuspenseQuery(suspenseHttpQuery(endpoint, options, useQueryOptions));
}

export function usePrefetchHttpQuery<T extends RegisteredHttpEndpoint>(endpoint: T, options: HttpOptions<T>): void {
    usePrefetchQuery({
        queryKey: httpQueryKey(endpoint, options),
        queryFn: async () => http(endpoint, options, getTokenIfPresent()),
    });
}

/** @deprecated Use `httpQueryKey`: `queryClient.invalidateQueries(httpQueryKey('GET /rfqs'))`. */
export async function invalidateAllQueriesForEndpoint(
    endpoint: RegisteredHttpEndpoint,
    queryClient: QueryClient,
): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: [endpoint] });
}

/** @deprecated Use `httpQueryKey`: `queryClient.invalidateQueries(httpQueryKey('GET /rfqs'))`. */
export function invalidateQuery<T extends RegisteredHttpEndpoint>(
    endpoint: T,
    options: HttpOptions<T>,
    queryClient: QueryClient,
): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: [endpoint, options] });
}
